import React, { useMemo, useCallback } from 'react';
import { format, eachDayOfInterval, isWeekend, addDays, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { JourDetail } from '@/types/calendar';

// Type pour la Map de sélection des jours
// Clé: "YYYY-MM-DD:AM" ou "YYYY-MM-DD:PM", Valeur: 0.5
export type SelectedWorkDaysMap = Map<string, number>;

interface DayPeriodSelectorProps {
  /** Date de début de la période */
  dateDebut: Date;
  /** Date de fin de la période */
  dateFin: Date;
  /** Map des jours sélectionnés (clé: "YYYY-MM-DD:AM|PM") */
  selectedDays: SelectedWorkDaysMap;
  /** Callback appelé quand la sélection change */
  onChange: (newSelection: SelectedWorkDaysMap) => void;
  /** Nombre total de jours attendus (optionnel, pour info) */
  expectedTotalDays?: number;
  /** Classe CSS additionnelle */
  className?: string;
}

/**
 * Convertir selectedWorkDays (Map) en format JSONB pour stockage
 * Format: [{ "date": "2026-01-03", "period": "AM", "value": 0.5 }, ...]
 */
export function convertWorkDaysToJsonb(selectedWorkDays: SelectedWorkDaysMap): JourDetail[] {
  const result: JourDetail[] = [];

  selectedWorkDays.forEach((value, key) => {
    const [dateStr, period] = key.split(':');
    result.push({
      date: dateStr,
      period: period as 'AM' | 'PM',
      value
    });
  });

  // Trier par date puis par période (AM avant PM)
  return result.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.period === 'AM' ? -1 : 1;
  });
}

/**
 * Convertir JSONB jours_details en Map pour utilisation dans le composant
 */
export function convertJsonbToWorkDays(joursDetails: JourDetail[] | null | undefined): SelectedWorkDaysMap {
  const map: SelectedWorkDaysMap = new Map();
  if (!joursDetails) return map;

  joursDetails.forEach(detail => {
    if (detail.period === 'FULL') {
      // Un jour complet = AM + PM
      map.set(`${detail.date}:AM`, 0.5);
      map.set(`${detail.date}:PM`, 0.5);
    } else {
      map.set(`${detail.date}:${detail.period}`, detail.value);
    }
  });

  return map;
}

/**
 * Calculer le total des jours sélectionnés
 */
export function calculateTotalSelectedDays(selectedDays: SelectedWorkDaysMap): number {
  let total = 0;
  selectedDays.forEach(value => {
    total += value;
  });
  return total;
}

/**
 * Générer une sélection automatique basée sur le nombre de jours
 * Remplit les jours ouvrés en priorité, weekends en dernier
 * La demi-journée (si présente) est positionnée en MATIN du DERNIER jour travaillé
 */
export function generateAutoSelection(
  dateDebut: Date,
  dateFin: Date,
  totalDays: number
): SelectedWorkDaysMap {
  const newMap: SelectedWorkDaysMap = new Map();
  const allDays = eachDayOfInterval({ start: dateDebut, end: dateFin });

  // Séparer jours ouvrés et weekends
  const workDays = allDays.filter(d => !isWeekend(d));
  const weekendDays = allDays.filter(d => isWeekend(d));

  // Calculer jours complets et demi-journée
  const joursComplets = Math.floor(totalDays);
  const hasHalfDay = totalDays % 1 !== 0; // 0.5 restant

  // Nombre total de jours nécessaires (complets + 1 si demi-journée)
  const nbJoursNecessaires = joursComplets + (hasHalfDay ? 1 : 0);

  // Sélectionner les jours à utiliser (ouvrés d'abord, weekends si nécessaire)
  const joursAUtiliser: Date[] = [];
  for (const day of workDays) {
    if (joursAUtiliser.length >= nbJoursNecessaires) break;
    joursAUtiliser.push(day);
  }
  // Ajouter des weekends si nécessaire
  for (const day of weekendDays) {
    if (joursAUtiliser.length >= nbJoursNecessaires) break;
    joursAUtiliser.push(day);
  }

  // Trier par date chronologique
  joursAUtiliser.sort((a, b) => a.getTime() - b.getTime());

  // Remplir les jours
  joursAUtiliser.forEach((day, index) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const isLastDay = index === joursAUtiliser.length - 1;

    if (isLastDay && hasHalfDay) {
      // Dernier jour avec demi-journée : uniquement le matin (AM)
      newMap.set(`${dateKey}:AM`, 0.5);
    } else {
      // Journée complète : AM + PM
      newMap.set(`${dateKey}:AM`, 0.5);
      newMap.set(`${dateKey}:PM`, 0.5);
    }
  });

  return newMap;
}

/**
 * Composant de sélection des demi-journées
 * Grille 7 colonnes (Lun-Dim) avec boutons M/A pour chaque jour
 */
export function DayPeriodSelector({
  dateDebut,
  dateFin,
  selectedDays,
  onChange,
  expectedTotalDays,
  className
}: DayPeriodSelectorProps) {
  // Générer tous les jours de la période
  // Étendre jusqu'à la fin de la semaine pour toujours afficher les weekends
  const daysInPeriod = useMemo(() => {
    if (!dateDebut || !dateFin || dateFin < dateDebut) return [];

    // Étendre la fin jusqu'au dimanche de la semaine (pour inclure le weekend)
    // weekStartsOn: 1 = lundi, donc dimanche = fin de semaine
    const extendedEnd = endOfWeek(dateFin, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: dateDebut, end: extendedEnd });
  }, [dateDebut, dateFin]);

  // Calculer le total sélectionné
  const totalSelectedDays = useMemo(() => {
    return calculateTotalSelectedDays(selectedDays);
  }, [selectedDays]);

  // Toggle une demi-journée
  const toggleHalfDay = useCallback((day: Date, period: 'AM' | 'PM') => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const key = `${dateKey}:${period}`;

    const newMap = new Map(selectedDays);
    if (newMap.has(key)) {
      newMap.delete(key);
    } else {
      newMap.set(key, 0.5);
    }
    onChange(newMap);
  }, [selectedDays, onChange]);

  // Vérifier si une demi-journée est sélectionnée
  const isHalfDaySelected = useCallback((day: Date, period: 'AM' | 'PM'): boolean => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return selectedDays.has(`${dateKey}:${period}`);
  }, [selectedDays]);

  // Vérifier si un jour complet est sélectionné
  const isFullDaySelected = useCallback((day: Date): boolean => {
    return isHalfDaySelected(day, 'AM') && isHalfDaySelected(day, 'PM');
  }, [isHalfDaySelected]);

  // Toggle jour complet (AM + PM)
  const toggleFullDay = useCallback((day: Date) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const amKey = `${dateKey}:AM`;
    const pmKey = `${dateKey}:PM`;

    const newMap = new Map(selectedDays);
    const hasAM = newMap.has(amKey);
    const hasPM = newMap.has(pmKey);

    if (hasAM && hasPM) {
      // Tout désélectionner
      newMap.delete(amKey);
      newMap.delete(pmKey);
    } else if (!hasAM && !hasPM) {
      // Tout sélectionner
      newMap.set(amKey, 0.5);
      newMap.set(pmKey, 0.5);
    } else {
      // Compléter la sélection
      if (!hasAM) newMap.set(amKey, 0.5);
      if (!hasPM) newMap.set(pmKey, 0.5);
    }
    onChange(newMap);
  }, [selectedDays, onChange]);

  if (daysInPeriod.length === 0) {
    return (
      <div className={cn("text-center text-muted-foreground py-4", className)}>
        Période invalide
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* En-tête avec totaux */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          Période : <span className="font-medium text-foreground">
            {format(dateDebut, 'd MMM', { locale: fr })} - {format(dateFin, 'd MMM yyyy', { locale: fr })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {expectedTotalDays !== undefined && (
            <span className="text-muted-foreground">
              Attendu : <span className="font-medium">{expectedTotalDays}j</span>
            </span>
          )}
          <span className={cn(
            "font-bold px-2 py-0.5 rounded",
            expectedTotalDays !== undefined && Math.abs(totalSelectedDays - expectedTotalDays) < 0.01
              ? "bg-green-500/20 text-green-400"
              : "bg-primary/20 text-primary"
          )}>
            {totalSelectedDays}j
          </span>
        </div>
      </div>

      {/* Instruction */}
      <p className="text-xs text-muted-foreground text-center">
        Cliquez sur <strong>M</strong> (matin) ou <strong>A</strong> (après-midi) pour une demi-journée,
        ou sur le <strong>numéro</strong> pour un jour complet
      </p>

      {/* Grille des jours */}
      <div className="space-y-2">
        {/* En-tête jours de la semaine */}
        <div className="grid grid-cols-7 gap-1.5 mb-2">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(jour => (
            <div key={jour} className="text-center text-xs font-medium text-muted-foreground">
              {jour}
            </div>
          ))}
        </div>

        {/* Grille avec décalage pour aligner au bon jour */}
        <div className="grid grid-cols-7 gap-1.5">
          {/* Décalage pour aligner avec le bon jour de la semaine */}
          {daysInPeriod.length > 0 && (
            <>
              {Array.from({ length: (daysInPeriod[0].getDay() + 6) % 7 }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
            </>
          )}

          {/* Jours avec deux demi-journées cliquables */}
          {daysInPeriod.map(day => {
            const isWeekendDay = isWeekend(day);
            const amSelected = isHalfDaySelected(day, 'AM');
            const pmSelected = isHalfDaySelected(day, 'PM');
            const isFullySelected = amSelected && pmSelected;
            // Vérifier si le jour est dans la période originale ou étendu (weekend après dateFin)
            const isExtendedDay = day > dateFin;

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "rounded-lg overflow-hidden border transition-all",
                  isWeekendDay
                    ? "border-orange-500/30 bg-orange-950/10"
                    : "border-border bg-card",
                  isFullySelected && "ring-2 ring-primary ring-offset-1 ring-offset-background",
                  // Jours étendus (hors période originale) - légèrement plus transparents
                  isExtendedDay && !amSelected && !pmSelected && "opacity-60"
                )}
              >
                {/* Numéro du jour - cliquable pour tout sélectionner/désélectionner */}
                <button
                  type="button"
                  onClick={() => toggleFullDay(day)}
                  className={cn(
                    "w-full text-center py-1 text-sm font-bold transition-colors",
                    isFullySelected
                      ? isWeekendDay ? "bg-orange-500 text-white" : "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  {format(day, 'd')}
                </button>

                {/* Conteneur des deux demi-journées */}
                <div className="flex">
                  {/* Matin */}
                  <button
                    type="button"
                    onClick={() => toggleHalfDay(day, 'AM')}
                    className={cn(
                      "flex-1 py-1.5 text-[10px] font-medium transition-all border-r border-border/50",
                      amSelected
                        ? isWeekendDay
                          ? "bg-orange-500 text-white"
                          : "bg-primary text-primary-foreground"
                        : "hover:bg-muted/80 text-muted-foreground"
                    )}
                  >
                    M
                  </button>

                  {/* Après-midi */}
                  <button
                    type="button"
                    onClick={() => toggleHalfDay(day, 'PM')}
                    className={cn(
                      "flex-1 py-1.5 text-[10px] font-medium transition-all",
                      pmSelected
                        ? isWeekendDay
                          ? "bg-orange-500 text-white"
                          : "bg-primary text-primary-foreground"
                        : "hover:bg-muted/80 text-muted-foreground"
                    )}
                  >
                    A
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Légende */}
      <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground justify-center">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded bg-primary" />
          <span>Sélectionné</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded bg-orange-500" />
          <span>Weekend travaillé</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded bg-muted border border-border" />
          <span>Non travaillé</span>
        </div>
      </div>
    </div>
  );
}

export default DayPeriodSelector;
